<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import {
  useAccessControlAdminStore,
  type PermissionAdminItem,
  type RoleGroupAdminListItem,
  type RoleGroupAdminDetail,
  type UserSearchResult,
} from '@/stores/useAccessControlAdminStore'
import {
  PlusIcon,
  TrashIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/vue/24/outline'

const store = useAccessControlAdminStore()

const activeTab = ref<'permissions' | 'groups'>('permissions')

// ----- Permissions state -----
const showPermissionModal = ref(false)
const editingPermission = ref<PermissionAdminItem | null>(null)
const permissionForm = ref({ code: '', description: '' })
const permissionSaving = ref(false)
const permissionError = ref<string | null>(null)

const showDeletePermissionModal = ref(false)
const permissionToDelete = ref<PermissionAdminItem | null>(null)
const deletePermissionError = ref<string | null>(null)

const permissionSearch = ref('')

const filteredPermissions = computed(() => {
  const q = permissionSearch.value.trim().toLowerCase()
  if (!q) return store.permissions
  return store.permissions.filter(
    (p) =>
      p.code.toLowerCase().includes(q) ||
      (p.description ?? '').toLowerCase().includes(q),
  )
})

function openCreatePermission() {
  editingPermission.value = null
  permissionForm.value = { code: '', description: '' }
  permissionError.value = null
  showPermissionModal.value = true
}

function openEditPermission(p: PermissionAdminItem) {
  editingPermission.value = p
  permissionForm.value = { code: p.code, description: p.description ?? '' }
  permissionError.value = null
  showPermissionModal.value = true
}

function closePermissionModal() {
  showPermissionModal.value = false
  editingPermission.value = null
  permissionError.value = null
}

async function submitPermission() {
  permissionSaving.value = true
  permissionError.value = null
  try {
    if (editingPermission.value) {
      const ok = await store.updatePermission(
        editingPermission.value.uuid,
        permissionForm.value.description.trim() || null,
      )
      if (ok) closePermissionModal()
      else permissionError.value = 'Erreur lors de la mise à jour'
    } else {
      const code = permissionForm.value.code.trim().toUpperCase()
      if (!code) {
        permissionError.value = 'Le code est obligatoire'
        return
      }
      const res = await store.createPermission({
        code,
        description: permissionForm.value.description.trim() || null,
      })
      if (res.ok) closePermissionModal()
      else permissionError.value = res.message
    }
  } finally {
    permissionSaving.value = false
  }
}

function askDeletePermission(p: PermissionAdminItem) {
  permissionToDelete.value = p
  deletePermissionError.value = null
  showDeletePermissionModal.value = true
}

async function confirmDeletePermission() {
  if (!permissionToDelete.value) return
  const res = await store.deletePermission(permissionToDelete.value.uuid)
  if (res.ok) {
    showDeletePermissionModal.value = false
    permissionToDelete.value = null
  } else {
    deletePermissionError.value = res.message
  }
}

// ----- Role groups state -----
const showGroupCreateModal = ref(false)
const groupCreateForm = ref({ name: '', description: '', adGroupDn: '' })
const groupCreateSelectedPerms = ref<Record<string, boolean>>({})
const groupSaving = ref(false)
const groupError = ref<string | null>(null)

const showGroupDetailModal = ref(false)
const groupDetail = ref<RoleGroupAdminDetail | null>(null)
const isLoadingGroupDetail = ref(false)
const groupEditForm = ref({ name: '', description: '', adGroupDn: '' })
const updatingGroup = ref(false)
const selectedPermissionToAdd = ref<string | null>(null)

const showDeleteGroupModal = ref(false)
const groupToDelete = ref<RoleGroupAdminListItem | null>(null)
const deleteGroupError = ref<string | null>(null)

const groupSearch = ref('')

const filteredGroups = computed(() => {
  const q = groupSearch.value.trim().toLowerCase()
  if (!q) return store.roleGroups
  return store.roleGroups.filter(
    (g) =>
      g.name.toLowerCase().includes(q) ||
      (g.description ?? '').toLowerCase().includes(q) ||
      g.permissions.some((p) => p.code.toLowerCase().includes(q)),
  )
})

const availablePermissionsForGroup = computed(() => {
  if (!groupDetail.value) return []
  const used = new Set(groupDetail.value.permissions.map((p) => p.uuid))
  return store.permissions
    .filter((p) => !used.has(p.uuid))
    .map((p) => ({ id: p.uuid, value: p.uuid, label: p.code }))
})

function openCreateGroup() {
  groupCreateForm.value = { name: '', description: '', adGroupDn: '' }
  groupCreateSelectedPerms.value = {}
  groupError.value = null
  showGroupCreateModal.value = true
}

function closeCreateGroup() {
  showGroupCreateModal.value = false
  groupError.value = null
}

async function submitCreateGroup() {
  if (!groupCreateForm.value.name.trim()) {
    groupError.value = 'Le nom est obligatoire'
    return
  }
  groupSaving.value = true
  groupError.value = null
  try {
    const permissionUuids = Object.entries(groupCreateSelectedPerms.value)
      .filter(([, v]) => v)
      .map(([k]) => k)
    const res = await store.createRoleGroup({
      name: groupCreateForm.value.name.trim(),
      description: groupCreateForm.value.description.trim() || null,
      adGroupDn: groupCreateForm.value.adGroupDn.trim() || null,
      permissionUuids: permissionUuids.length ? permissionUuids : undefined,
    })
    if (res.ok) closeCreateGroup()
    else groupError.value = res.message
  } finally {
    groupSaving.value = false
  }
}

async function openGroupDetail(g: RoleGroupAdminListItem) {
  groupDetail.value = null
  isLoadingGroupDetail.value = true
  showGroupDetailModal.value = true
  selectedPermissionToAdd.value = null
  const fetched = await store.fetchRoleGroupDetail(g.uuid)
  if (fetched) {
    groupDetail.value = fetched
    groupEditForm.value = {
      name: fetched.name,
      description: fetched.description ?? '',
      adGroupDn: fetched.adGroupDn ?? '',
    }
  }
  isLoadingGroupDetail.value = false
}

function closeGroupDetail() {
  showGroupDetailModal.value = false
  groupDetail.value = null
  selectedPermissionToAdd.value = null
  memberSearch.value = ''
  memberSearchResults.value = []
}

// ----- Gestion des membres du groupe -----
const memberSearch = ref('')
const memberSearchResults = ref<UserSearchResult[]>([])
const memberSearchLoading = ref(false)
const memberActionUuid = ref<string | null>(null)
let memberSearchTimer: ReturnType<typeof setTimeout> | undefined

watch(memberSearch, (q) => {
  clearTimeout(memberSearchTimer)
  const term = q.trim()
  if (term.length < 2) {
    memberSearchResults.value = []
    return
  }
  memberSearchTimer = setTimeout(async () => {
    memberSearchLoading.value = true
    try {
      const results = await store.searchUsers(term)
      const memberUuids = new Set(groupDetail.value?.members.map((m) => m.userUuid) ?? [])
      memberSearchResults.value = results.filter((u) => !memberUuids.has(u.uuid))
    } finally {
      memberSearchLoading.value = false
    }
  }, 300)
})

async function refreshGroupDetail() {
  if (!groupDetail.value) return
  const fresh = await store.fetchRoleGroupDetail(groupDetail.value.uuid)
  if (fresh) {
    groupDetail.value = fresh
    const idx = store.roleGroups.findIndex((g) => g.uuid === fresh.uuid)
    if (idx >= 0) store.roleGroups[idx].membersCount = fresh.membersCount
  }
}

async function addMemberToCurrentGroup(user: UserSearchResult) {
  if (!groupDetail.value) return
  memberActionUuid.value = user.uuid
  try {
    const ok = await store.addMemberToGroup(groupDetail.value.uuid, user.uuid)
    if (ok) {
      memberSearch.value = ''
      memberSearchResults.value = []
      await refreshGroupDetail()
    }
  } finally {
    memberActionUuid.value = null
  }
}

async function removeMemberFromCurrentGroup(membershipUuid: string) {
  if (!groupDetail.value) return
  memberActionUuid.value = membershipUuid
  try {
    const ok = await store.removeMemberFromGroup(groupDetail.value.uuid, membershipUuid)
    if (ok) await refreshGroupDetail()
  } finally {
    memberActionUuid.value = null
  }
}

function userLabel(u: { email: string; firstName: string | null; lastName: string | null }) {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ')
  return name ? `${name} — ${u.email}` : u.email
}

async function saveGroupEdit() {
  if (!groupDetail.value) return
  updatingGroup.value = true
  try {
    const ok = await store.updateRoleGroup(groupDetail.value.uuid, {
      name: groupEditForm.value.name.trim() || undefined,
      description: groupEditForm.value.description.trim() || null,
      adGroupDn: groupEditForm.value.adGroupDn.trim() || null,
    })
    if (ok) {
      const fresh = await store.fetchRoleGroupDetail(groupDetail.value.uuid)
      if (fresh) groupDetail.value = fresh
    }
  } finally {
    updatingGroup.value = false
  }
}

async function addPermissionToCurrentGroup() {
  if (!groupDetail.value || !selectedPermissionToAdd.value) return
  const ok = await store.addPermissionToGroup(groupDetail.value.uuid, selectedPermissionToAdd.value)
  if (ok) {
    const perm = store.permissions.find((p) => p.uuid === selectedPermissionToAdd.value)
    if (perm) {
      groupDetail.value.permissions.push({
        uuid: perm.uuid,
        code: perm.code,
        description: perm.description,
      })
    }
    selectedPermissionToAdd.value = null
    // Met à jour le compteur côté liste
    const idx = store.roleGroups.findIndex((g) => g.uuid === groupDetail.value!.uuid)
    if (idx >= 0 && perm) {
      store.roleGroups[idx].permissions.push({
        uuid: perm.uuid,
        code: perm.code,
        description: perm.description,
      })
    }
  }
}

async function removePermissionFromCurrentGroup(permissionUuid: string) {
  if (!groupDetail.value) return
  const ok = await store.removePermissionFromGroup(groupDetail.value.uuid, permissionUuid)
  if (ok) {
    groupDetail.value.permissions = groupDetail.value.permissions.filter((p) => p.uuid !== permissionUuid)
    const idx = store.roleGroups.findIndex((g) => g.uuid === groupDetail.value!.uuid)
    if (idx >= 0) {
      store.roleGroups[idx].permissions = store.roleGroups[idx].permissions.filter((p) => p.uuid !== permissionUuid)
    }
  }
}

function askDeleteGroup(g: RoleGroupAdminListItem) {
  groupToDelete.value = g
  deleteGroupError.value = null
  showDeleteGroupModal.value = true
}

async function confirmDeleteGroup() {
  if (!groupToDelete.value) return
  const res = await store.deleteRoleGroup(groupToDelete.value.uuid)
  if (res.ok) {
    showDeleteGroupModal.value = false
    groupToDelete.value = null
  } else {
    deleteGroupError.value = res.message
  }
}

function formatDate(s: string | null | undefined) {
  if (!s) return '—'
  return new Date(s).toLocaleString('fr-FR')
}

onMounted(async () => {
  await Promise.all([store.fetchPermissions(), store.fetchRoleGroups()])
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Permissions & groupes" subtitle="SUPER_ADMIN uniquement" />

    <div class="flex flex-wrap gap-2 mt-4">
      <router-link to="/dashboard/admin" class="btn btn-outline btn-sm">← Retour dashboard admin</router-link>
      <router-link to="/admin/users" class="btn btn-outline btn-sm">Utilisateurs</router-link>
    </div>

    <div role="tablist" class="tabs tabs-boxed mt-6 p-1 bg-base-200 rounded-lg w-fit">
      <a
        role="tab"
        :class="['tab px-4 py-2 rounded-md font-medium', activeTab === 'permissions' ? 'tab-active bg-base-100 shadow-sm' : '']"
        @click="activeTab = 'permissions'"
      >
        <ShieldCheckIcon class="w-4 h-4 inline mr-1" />
        Permissions ({{ store.permissions.length }})
      </a>
      <a
        role="tab"
        :class="['tab px-4 py-2 rounded-md font-medium', activeTab === 'groups' ? 'tab-active bg-base-100 shadow-sm' : '']"
        @click="activeTab = 'groups'"
      >
        <UserGroupIcon class="w-4 h-4 inline mr-1" />
        Groupes ({{ store.roleGroups.length }})
      </a>
    </div>

    <!-- ============== PERMISSIONS ============== -->
    <div v-show="activeTab === 'permissions'" class="mt-4">
      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <div class="flex items-center justify-between mb-4 gap-2">
            <input
              v-model="permissionSearch"
              type="text"
              placeholder="Rechercher (code, description)…"
              class="input input-bordered input-sm w-64"
            />
            <div class="flex gap-2">
              <ButtonDaisy
                text="Actualiser"
                variant="outline"
                size="sm"
                @click="store.fetchPermissions()"
              />
              <button class="btn btn-primary btn-sm gap-1" @click="openCreatePermission">
                <PlusIcon class="w-4 h-4" />
                Nouvelle permission
              </button>
            </div>
          </div>

          <div v-if="store.isLoadingPermissions" class="py-8 text-center">
            <span class="loading loading-spinner loading-lg" />
          </div>
          <div v-else-if="filteredPermissions.length === 0" class="py-8 text-center text-base-content/60">
            Aucune permission.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th class="text-center">Groupes</th>
                  <th class="w-32"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in filteredPermissions" :key="p.uuid" class="hover">
                  <td class="font-mono font-semibold">{{ p.code }}</td>
                  <td class="text-sm">{{ p.description ?? '—' }}</td>
                  <td class="text-center">
                    <span class="badge badge-ghost">{{ p.roleGroupsCount }}</span>
                  </td>
                  <td>
                    <div class="flex gap-1 justify-end">
                      <button class="btn btn-ghost btn-xs" title="Modifier" @click="openEditPermission(p)">
                        Modifier
                      </button>
                      <button
                        class="btn btn-ghost btn-xs text-error"
                        title="Supprimer"
                        :disabled="p.roleGroupsCount > 0"
                        @click="askDeletePermission(p)"
                      >
                        <TrashIcon class="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ============== GROUPES ============== -->
    <div v-show="activeTab === 'groups'" class="mt-4">
      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <div class="flex items-center justify-between mb-4 gap-2">
            <input
              v-model="groupSearch"
              type="text"
              placeholder="Rechercher (nom, description, permission)…"
              class="input input-bordered input-sm w-72"
            />
            <div class="flex gap-2">
              <ButtonDaisy
                text="Actualiser"
                variant="outline"
                size="sm"
                @click="store.fetchRoleGroups()"
              />
              <button class="btn btn-primary btn-sm gap-1" @click="openCreateGroup">
                <PlusIcon class="w-4 h-4" />
                Nouveau groupe
              </button>
            </div>
          </div>

          <div v-if="store.isLoadingRoleGroups" class="py-8 text-center">
            <span class="loading loading-spinner loading-lg" />
          </div>
          <div v-else-if="filteredGroups.length === 0" class="py-8 text-center text-base-content/60">
            Aucun groupe.
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="g in filteredGroups"
              :key="g.uuid"
              class="p-4 bg-base-200 rounded-lg flex items-start justify-between gap-2"
            >
              <div class="flex-1">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-semibold">{{ g.name }}</span>
                  <span class="badge badge-info badge-sm">{{ g.membersCount }} membre(s)</span>
                  <span class="badge badge-ghost badge-sm">{{ g.permissions.length }} permission(s)</span>
                </div>
                <p v-if="g.description" class="text-xs text-base-content/60 mt-1">{{ g.description }}</p>
                <p v-if="g.adGroupDn" class="text-xs text-base-content/50 font-mono mt-0.5">AD : {{ g.adGroupDn }}</p>
                <div class="flex flex-wrap gap-1 mt-2">
                  <span
                    v-for="p in g.permissions"
                    :key="p.uuid"
                    class="badge badge-sm badge-ghost"
                    :title="p.description ?? ''"
                  >
                    {{ p.code }}
                  </span>
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <button class="btn btn-ghost btn-xs" @click="openGroupDetail(g)">Voir détail</button>
                <button
                  class="btn btn-ghost btn-xs text-error"
                  :disabled="g.membersCount > 0"
                  @click="askDeleteGroup(g)"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Modal create/edit permission ===== -->
    <div class="modal" :class="{ 'modal-open': showPermissionModal }">
      <div class="modal-box max-w-md">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <ShieldCheckIcon class="w-6 h-6" />
          {{ editingPermission ? `Modifier ${editingPermission.code}` : 'Nouvelle permission' }}
        </h3>
        <div class="mt-4 space-y-4">
          <div v-if="!editingPermission" class="form-control">
            <label class="label"><span class="label-text">Code (MAJUSCULES_AVEC_UNDERSCORES)</span></label>
            <input
              v-model="permissionForm.code"
              type="text"
              class="input input-bordered input-sm font-mono"
              placeholder="USER_MASTER"
              :disabled="permissionSaving"
              @input="permissionForm.code = permissionForm.code.toUpperCase().replace(/[^A-Z0-9_]/g, '_')"
            />
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">Description</span></label>
            <textarea
              v-model="permissionForm.description"
              class="textarea textarea-bordered textarea-sm"
              rows="2"
              :disabled="permissionSaving"
            />
          </div>
          <div v-if="permissionError" class="alert alert-error text-sm">{{ permissionError }}</div>
        </div>
        <div class="modal-action">
          <button class="btn" :disabled="permissionSaving" @click="closePermissionModal">Annuler</button>
          <button class="btn btn-primary" :disabled="permissionSaving" @click="submitPermission">
            {{ permissionSaving ? 'Enregistrement…' : (editingPermission ? 'Enregistrer' : 'Créer') }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closePermissionModal" />
    </div>

    <!-- ===== Modal create group ===== -->
    <div class="modal" :class="{ 'modal-open': showGroupCreateModal }">
      <div class="modal-box max-w-2xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <UserGroupIcon class="w-6 h-6" />
          Nouveau groupe
        </h3>
        <div class="mt-4 space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div class="form-control">
              <label class="label"><span class="label-text">Nom</span></label>
              <input
                v-model="groupCreateForm.name"
                type="text"
                class="input input-bordered input-sm"
                :disabled="groupSaving"
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">AD Group DN (optionnel)</span></label>
              <input
                v-model="groupCreateForm.adGroupDn"
                type="text"
                class="input input-bordered input-sm font-mono"
                :disabled="groupSaving"
              />
            </div>
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">Description</span></label>
            <textarea
              v-model="groupCreateForm.description"
              class="textarea textarea-bordered textarea-sm"
              rows="2"
              :disabled="groupSaving"
            />
          </div>
          <div>
            <label class="label"><span class="label-text">Permissions à inclure</span></label>
            <div class="max-h-60 overflow-y-auto p-2 bg-base-200 rounded space-y-1">
              <label
                v-for="p in store.permissions"
                :key="p.uuid"
                class="flex items-center gap-2 cursor-pointer hover:bg-base-300 p-1 rounded"
              >
                <input
                  v-model="groupCreateSelectedPerms[p.uuid]"
                  type="checkbox"
                  class="checkbox checkbox-sm"
                />
                <span class="font-mono text-sm">{{ p.code }}</span>
                <span v-if="p.description" class="text-xs text-base-content/60 truncate">{{ p.description }}</span>
              </label>
              <p v-if="store.permissions.length === 0" class="text-sm text-base-content/60 p-2">
                Aucune permission. Crée des permissions d'abord.
              </p>
            </div>
          </div>
          <div v-if="groupError" class="alert alert-error text-sm">{{ groupError }}</div>
        </div>
        <div class="modal-action">
          <button class="btn" :disabled="groupSaving" @click="closeCreateGroup">Annuler</button>
          <button class="btn btn-primary" :disabled="groupSaving" @click="submitCreateGroup">
            {{ groupSaving ? 'Création…' : 'Créer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeCreateGroup" />
    </div>

    <!-- ===== Modal group detail ===== -->
    <div class="modal" :class="{ 'modal-open': showGroupDetailModal }">
      <div class="modal-box max-w-3xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <UserGroupIcon class="w-6 h-6" />
          {{ groupDetail?.name ?? '…' }}
        </h3>
        <div v-if="isLoadingGroupDetail" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>
        <div v-else-if="groupDetail" class="mt-4 space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div class="form-control">
              <label class="label"><span class="label-text">Nom</span></label>
              <input v-model="groupEditForm.name" type="text" class="input input-bordered input-sm" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">AD Group DN</span></label>
              <input v-model="groupEditForm.adGroupDn" type="text" class="input input-bordered input-sm font-mono" />
            </div>
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">Description</span></label>
            <textarea v-model="groupEditForm.description" class="textarea textarea-bordered textarea-sm" rows="2" />
          </div>

          <div class="border-t pt-4">
            <h4 class="font-medium text-sm mb-2 flex items-center gap-2">
              <ShieldCheckIcon class="w-4 h-4" />
              Permissions ({{ groupDetail.permissions.length }})
            </h4>
            <div v-if="groupDetail.permissions.length === 0" class="text-sm text-base-content/60">
              Aucune permission dans ce groupe.
            </div>
            <div v-else class="flex flex-wrap gap-1">
              <span
                v-for="p in groupDetail.permissions"
                :key="p.uuid"
                class="badge badge-md badge-ghost gap-1"
                :title="p.description ?? ''"
              >
                {{ p.code }}
                <button
                  type="button"
                  class="ml-1 hover:text-error"
                  title="Retirer"
                  @click="removePermissionFromCurrentGroup(p.uuid)"
                >
                  ×
                </button>
              </span>
            </div>
            <div v-if="availablePermissionsForGroup.length" class="flex items-end gap-2 mt-3">
              <SelectInput
                v-model="selectedPermissionToAdd"
                label="Ajouter une permission"
                :options="availablePermissionsForGroup"
                placeholder="Sélectionner…"
                class="flex-1 min-w-48"
              />
              <button
                class="btn btn-primary btn-sm"
                :disabled="!selectedPermissionToAdd"
                @click="addPermissionToCurrentGroup"
              >
                Ajouter
              </button>
            </div>
          </div>

          <div class="border-t pt-4">
            <h4 class="font-medium text-sm mb-2 flex items-center gap-2">
              <UserGroupIcon class="w-4 h-4" />
              Membres ({{ groupDetail.members.length }})
            </h4>
            <!-- Ajout rapide d'un membre -->
            <div class="relative mb-3 max-w-md">
              <input
                v-model="memberSearch"
                type="text"
                class="input input-bordered input-sm w-full"
                placeholder="Ajouter un utilisateur (nom ou email)…"
              />
              <div
                v-if="memberSearch.trim().length >= 2"
                class="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-box border border-base-300 bg-base-100 shadow"
              >
                <div v-if="memberSearchLoading" class="p-2 text-sm text-base-content/60">Recherche…</div>
                <div v-else-if="memberSearchResults.length === 0" class="p-2 text-sm text-base-content/60">
                  Aucun utilisateur trouvé
                </div>
                <button
                  v-for="u in memberSearchResults"
                  :key="u.uuid"
                  type="button"
                  class="block w-full px-3 py-1.5 text-left text-sm hover:bg-base-200 disabled:opacity-50"
                  :disabled="memberActionUuid === u.uuid"
                  @click="addMemberToCurrentGroup(u)"
                >
                  {{ userLabel(u) }}
                </button>
              </div>
            </div>

            <div v-if="groupDetail.members.length === 0" class="text-sm text-base-content/60">
              Aucun membre pour l'instant.
            </div>
            <div v-else class="overflow-x-auto max-h-72">
              <table class="table table-xs">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Nom</th>
                    <th>Statut</th>
                    <th>Ajouté le</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in groupDetail.members" :key="m.membershipUuid">
                    <td>{{ m.email }}</td>
                    <td>{{ [m.firstName, m.lastName].filter(Boolean).join(' ') || '—' }}</td>
                    <td>
                      <span
                        class="badge badge-xs"
                        :class="m.status === 'ACTIVE' ? 'badge-success' : 'badge-error'"
                      >
                        {{ m.status }}
                      </span>
                    </td>
                    <td>{{ formatDate(m.joinedAt) }}</td>
                    <td class="text-right">
                      <button
                        type="button"
                        class="btn btn-ghost btn-xs text-error"
                        title="Retirer du groupe"
                        :disabled="memberActionUuid === m.membershipUuid"
                        @click="removeMemberFromCurrentGroup(m.membershipUuid)"
                      >
                        <TrashIcon class="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-action">
          <button class="btn" @click="closeGroupDetail">Fermer</button>
          <button
            v-if="groupDetail"
            class="btn btn-primary"
            :disabled="updatingGroup"
            @click="saveGroupEdit"
          >
            {{ updatingGroup ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeGroupDetail" />
    </div>

    <ConfirmModal
      :show-modal="showDeletePermissionModal"
      title="Supprimer la permission"
      :body="`Supprimer « ${permissionToDelete?.code ?? ''} » ? Cette action est irréversible.`"
      confirm-text="Supprimer"
      confirm-variant="error"
      @confirm="confirmDeletePermission"
      @cancel="showDeletePermissionModal = false; permissionToDelete = null; deletePermissionError = null"
    />
    <ConfirmModal
      :show-modal="showDeleteGroupModal"
      title="Supprimer le groupe"
      :body="`Supprimer « ${groupToDelete?.name ?? ''} » ? Cette action est irréversible.`"
      confirm-text="Supprimer"
      confirm-variant="error"
      @confirm="confirmDeleteGroup"
      @cancel="showDeleteGroupModal = false; groupToDelete = null; deleteGroupError = null"
    />
    <div v-if="deletePermissionError" class="toast toast-end">
      <div class="alert alert-error text-sm">{{ deletePermissionError }}</div>
    </div>
    <div v-if="deleteGroupError" class="toast toast-end">
      <div class="alert alert-error text-sm">{{ deleteGroupError }}</div>
    </div>
  </div>
</template>
